import React, { useMemo, useState, useEffect } from 'react';
import { createOrder } from '../services/orderService';
import AuthPanel from './AuthPanel';
import { getDeliveryPlan } from '../utils/deliverySlots';
import { subscribeToSettings } from '../services/settingsService';
import { isAddressServiceable } from '../utils/serviceabilityCheck';
import NotServiceableScreen from './NotServiceableScreen';
import { useNavigationGuard } from '../navigation';

const CartPanel = ({ cart, onUpdateQuantity, onRemove, onClose, onClearCart, user, addresses, onAuthReady, onOpenAddresses, onOrderSuccess }) => {
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [isPlacing, setIsPlacing] = useState(false);
    const [orderError, setOrderError] = useState('');
    const [showNotServiceable, setShowNotServiceable] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [settings, setSettings] = useState(null);
    // Autodetect address/location feature removed from cart page

    // Prevent accidental navigation during order placement
    useNavigationGuard({
      id: 'checkout-placing-order',
      isActive: isPlacing,
      message: 'Your order is being placed. Please wait until it completes.',
    });

    useEffect(() => {
        const unsubscribe = subscribeToSettings((newSettings) => {
            setSettings(newSettings);
        });
        return () => unsubscribe();
    }, []);

    const MIN_ORDER_AMOUNT = settings?.minOrderValue || 0;
    const DELIVERY_FEE = settings?.deliveryFee || 0;
    const DELIVERY_RADIUS = settings?.deliveryRadius || 0;

    const subtotal = useMemo(() => cart.reduce((total, item) => total + item.price * item.quantity, 0), [cart]);
    const deliveryFee = subtotal < MIN_ORDER_AMOUNT ? DELIVERY_FEE : 0;
    const cartTotal = subtotal + deliveryFee;

    const activeAddress = useMemo(() => {
        if (typeof selectedAddress === 'number' && addresses[selectedAddress]) {
            return addresses[selectedAddress];
        }
        return null;
    }, [selectedAddress, addresses]);

    const effectiveCoords = useMemo(() => {
        if (activeAddress?.latitude && activeAddress?.longitude) {
            return {
                latitude: Number(activeAddress.latitude),
                longitude: Number(activeAddress.longitude),
            };
        }
        return null;
    }, [activeAddress]);

    const deliveryPlan = useMemo(() => {
        if (!effectiveCoords) {
            return { distanceKm: null, ruleText: '', estimatedTime: '' };
        }
        return getDeliveryPlan(effectiveCoords);
    }, [effectiveCoords]);

    const selectedSlot = useMemo(() => deliveryPlan.estimatedTime ? { id: 'estimated', label: deliveryPlan.estimatedTime } : null, [deliveryPlan.estimatedTime]);
    
    useEffect(() => {
        if (deliveryPlan.estimatedTime) {
            // Auto-select estimated delivery time
        }
    }, [deliveryPlan.estimatedTime]);

    const handlePlaceOrder = async () => {
        if (!user?.uid) {
            setOrderError('Please sign in to place an order.');
            return;
        }
        if (!activeAddress) {
            setOrderError('Please select a delivery address.');
            return;
        }
        if (!effectiveCoords) {
            setOrderError('Could not determine coordinates for the selected address.');
            return;
        }
        
        // Check if address is serviceable
        if (!isAddressServiceable(activeAddress)) {
            setShowNotServiceable(true);
            return;
        }
        
        if (deliveryPlan.band === 'out_of_range') {
            setOrderError(`This address is outside our ${DELIVERY_RADIUS} km delivery radius.`);
            return;
        }
        if (!selectedSlot) {
            setOrderError('Could not determine delivery time.');
            return;
        }

        try {
            setOrderError('');
            setIsPlacing(true);

            const orderData = {
                items: cart.map(item => ({ id: item.id, name: item.name, price: item.price, quantity: item.quantity, category: item.category })),
                subtotal,
                deliveryFee,
                total: cartTotal,
                deliveryAddress: {
                    label: activeAddress.label,
                    line1: activeAddress.line1,
                    line2: activeAddress.line2,
                    city: activeAddress.city,
                    pincode: activeAddress.pincode,
                    location: activeAddress.location,
                    latitude: effectiveCoords.latitude,
                    longitude: effectiveCoords.longitude,
                    placeName: activeAddress.placeName || '',
                },
                deliveryDistanceKm: Number(deliveryPlan.distanceKm?.toFixed(2)),
                deliverySlotRule: deliveryPlan.ruleText,
                estimatedDeliveryTime: deliveryPlan.estimatedTime,
            };

            const orderId = await createOrder(user.uid, orderData);
            
            // Keep the placing screen visible long enough to be seen clearly
            await new Promise(resolve => setTimeout(resolve, 2400));
            
            setShowSuccessScreen(true);
            setIsPlacing(false);

            setTimeout(() => {
                setShowSuccessScreen(false);
                setSelectedAddress(null);
                onClearCart();
                if (onOrderSuccess) onOrderSuccess(orderId);
            }, 2600);
        } catch (error) {
            setOrderError(error.message || 'Failed to place order. Please try again.');
            setIsPlacing(false);
        }
    };

    if (isPlacing) {
        return (
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'radial-gradient(circle at top, rgba(255, 173, 92, 0.22), transparent 28%), linear-gradient(180deg, #fff8f3 0%, #ffefe3 48%, #ffe0c8 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                color: '#1f2937',
                padding: '24px'
            }}>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle at 18% 20%, rgba(255,255,255,0.7), transparent 24%), radial-gradient(circle at 82% 78%, rgba(255,255,255,0.45), transparent 20%)',
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'relative',
                    width: 'min(100%, 380px)',
                    borderRadius: '32px',
                    padding: '28px 24px',
                    background: 'rgba(255,255,255,0.78)',
                    border: '1px solid rgba(255,255,255,0.7)',
                    boxShadow: '0 28px 60px rgba(255, 107, 53, 0.18)',
                    backdropFilter: 'blur(18px)',
                    textAlign: 'center',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        position: 'absolute',
                        top: '-48px',
                        right: '-40px',
                        width: '140px',
                        height: '140px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255, 138, 91, 0.28), rgba(255, 138, 91, 0) 72%)',
                    }} />
                    <div style={{
                        position: 'relative',
                        width: '128px',
                        height: '128px',
                        margin: '0 auto 22px',
                        display: 'grid',
                        placeItems: 'center',
                    }}>
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background: 'conic-gradient(from 180deg, rgba(255,138,91,0.16), rgba(255,107,53,0.62), rgba(255,199,122,0.18), rgba(255,138,91,0.16))',
                            animation: 'orderOrbit 2.2s linear infinite',
                        }} />
                        <div style={{
                            position: 'absolute',
                            inset: '10px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #fff7f0 0%, #ffffff 100%)',
                            boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.8), 0 14px 26px rgba(255, 107, 53, 0.14)',
                        }} />
                        <div style={{
                            position: 'relative',
                            width: '84px',
                            height: '84px',
                            borderRadius: '26px',
                            background: 'linear-gradient(135deg, #ff9a5a 0%, #ff6b35 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '40px',
                            color: 'white',
                            boxShadow: '0 16px 28px rgba(255, 107, 53, 0.28)',
                            animation: 'orderFloat 1.8s ease-in-out infinite',
                        }}>
                            📦
                        </div>
                    </div>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '7px 12px',
                        borderRadius: '999px',
                        background: 'rgba(255, 244, 237, 0.95)',
                        border: '1px solid rgba(255, 138, 91, 0.25)',
                        color: '#f97316',
                        fontSize: '12px',
                        fontWeight: '800',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        marginBottom: '14px',
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#ff6b35',
                            animation: 'statusPulse 1.2s ease-in-out infinite',
                        }} />
                        Processing order
                    </div>
                    <h2 style={{
                        fontSize: '29px',
                        marginBottom: '10px',
                        fontWeight: '800',
                        color: '#111827',
                        letterSpacing: '-0.03em',
                        animation: 'fadeUp 0.55s ease-out',
                    }}>Placing your order</h2>
                    <p style={{
                        fontSize: '15px',
                        color: '#6b7280',
                        lineHeight: '1.65',
                        margin: '0 auto 18px',
                        maxWidth: '260px',
                        animation: 'fadeUp 0.55s ease-out 0.08s both',
                    }}>
                        Securing payment, confirming items, and preparing your delivery details.
                    </p>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '8px',
                        marginBottom: '18px',
                    }}>
                        {[0, 1, 2].map((dot) => (
                            <span key={dot} style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                background: dot === 1 ? '#ff8a5b' : '#ffd5bf',
                                animation: `loadingDots 1.2s ease-in-out ${dot * 0.16}s infinite`,
                            }} />
                        ))}
                    </div>
                    <div style={{
                        width: '100%',
                        height: '10px',
                        borderRadius: '999px',
                        background: 'rgba(255, 225, 205, 0.9)',
                        overflow: 'hidden',
                    }}>
                        <div style={{
                            width: '42%',
                            height: '100%',
                            borderRadius: '999px',
                            background: 'linear-gradient(90deg, #ffb37b 0%, #ff6b35 100%)',
                            boxShadow: '0 6px 16px rgba(255, 107, 53, 0.25)',
                            animation: 'loadingBar 1.4s ease-in-out infinite',
                        }} />
                    </div>
                </div>
                <style>{`
                    @keyframes orderOrbit {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes orderFloat {
                        0%, 100% { transform: translateY(0px) scale(1); }
                        50% { transform: translateY(-6px) scale(1.03); }
                    }
                    @keyframes statusPulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.35); opacity: 0.7; }
                    }
                    @keyframes fadeUp {
                        0% { opacity: 0; transform: translateY(16px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes loadingDots {
                        0%, 100% { transform: translateY(0); opacity: 0.45; }
                        50% { transform: translateY(-5px); opacity: 1; }
                    }
                    @keyframes loadingBar {
                        0% { transform: translateX(-110%); }
                        100% { transform: translateX(320%); }
                    }
                `}</style>
            </div>
        );
    }

    if (showSuccessScreen) {
        return (
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'radial-gradient(circle at top, rgba(134, 239, 172, 0.22), transparent 28%), linear-gradient(180deg, #f3fff8 0%, #dcfce7 50%, #c6f6d5 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                color: '#14532d',
                padding: '24px'
            }}>
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'radial-gradient(circle at 20% 18%, rgba(255,255,255,0.7), transparent 24%), radial-gradient(circle at 80% 82%, rgba(255,255,255,0.5), transparent 22%)',
                    pointerEvents: 'none',
                }} />
                <div style={{
                    position: 'relative',
                    width: 'min(100%, 390px)',
                    borderRadius: '34px',
                    padding: '30px 24px 28px',
                    background: 'rgba(255,255,255,0.82)',
                    border: '1px solid rgba(255,255,255,0.72)',
                    boxShadow: '0 28px 60px rgba(16, 185, 129, 0.18)',
                    backdropFilter: 'blur(18px)',
                    textAlign: 'center',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        background: 'radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%)',
                    }} />
                    <div style={{
                        position: 'relative',
                        width: '132px',
                        height: '132px',
                        margin: '0 auto 22px',
                        display: 'grid',
                        placeItems: 'center',
                    }}>
                        <div style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(34,197,94,0.18), rgba(34,197,94,0) 70%)',
                            animation: 'successGlow 2s ease-in-out infinite',
                        }} />
                        <div style={{
                            position: 'absolute',
                            inset: '8px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #ecfdf5 0%, #ffffff 100%)',
                            boxShadow: 'inset 0 2px 10px rgba(255,255,255,0.8), 0 14px 30px rgba(16, 185, 129, 0.14)',
                        }} />
                        <div style={{
                            position: 'relative',
                            width: '90px',
                            height: '90px',
                            borderRadius: '28px',
                            background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 18px 30px rgba(16, 185, 129, 0.26)',
                            animation: 'successPop 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
                        }}>
                            <svg width="56" height="56" viewBox="0 0 52 52">
                                <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="3"/>
                                <path fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" d="M16 27l7 7L37 20" style={{ strokeDasharray: 40, strokeDashoffset: 40, animation: 'checkDraw 0.55s ease-out 0.28s forwards' }}/>
                            </svg>
                        </div>
                        <span style={{ position: 'absolute', top: '6px', right: '12px', fontSize: '20px', animation: 'confettiFloat 1.6s ease-in-out infinite' }}>✨</span>
                        <span style={{ position: 'absolute', left: '10px', bottom: '10px', fontSize: '18px', animation: 'confettiFloat 1.6s ease-in-out 0.25s infinite' }}>🎉</span>
                    </div>
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '7px 12px',
                        borderRadius: '999px',
                        background: 'rgba(236, 253, 245, 0.98)',
                        border: '1px solid rgba(16, 185, 129, 0.18)',
                        color: '#059669',
                        fontSize: '12px',
                        fontWeight: '800',
                        letterSpacing: '0.04em',
                        textTransform: 'uppercase',
                        marginBottom: '14px',
                    }}>
                        <span style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#10b981',
                            boxShadow: '0 0 0 6px rgba(16,185,129,0.12)',
                        }} />
                        Confirmed
                    </div>
                    <h2 style={{
                        fontSize: '29px',
                        marginBottom: '10px',
                        fontWeight: '800',
                        color: '#14532d',
                        letterSpacing: '-0.03em',
                        animation: 'fadeUp 0.55s ease-out 0.18s both',
                    }}>Order placed successfully</h2>
                    <p style={{
                        fontSize: '15px',
                        color: '#4b5563',
                        lineHeight: '1.65',
                        margin: '0 auto 18px',
                        maxWidth: '270px',
                        animation: 'fadeUp 0.55s ease-out 0.3s both',
                    }}>
                        Your order is confirmed and we’re getting it ready for delivery.
                    </p>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: '10px',
                        marginTop: '8px',
                    }}>
                        <div style={{
                            padding: '12px 10px',
                            borderRadius: '18px',
                            background: 'rgba(255,255,255,0.92)',
                            border: '1px solid rgba(209, 250, 229, 0.95)',
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Status</div>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#059669' }}>Confirmed</div>
                        </div>
                        <div style={{
                            padding: '12px 10px',
                            borderRadius: '18px',
                            background: 'rgba(255,255,255,0.92)',
                            border: '1px solid rgba(209, 250, 229, 0.95)',
                        }}>
                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Next step</div>
                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#14532d' }}>Preparing items</div>
                        </div>
                    </div>
                </div>
                <style>{`
                    @keyframes successGlow {
                        0%, 100% { transform: scale(0.96); opacity: 0.65; }
                        50% { transform: scale(1.06); opacity: 1; }
                    }
                    @keyframes successPop {
                        0% { transform: scale(0.4) rotate(-8deg); opacity: 0; }
                        70% { transform: scale(1.08) rotate(0deg); opacity: 1; }
                        100% { transform: scale(1); opacity: 1; }
                    }
                    @keyframes checkDraw {
                        to { stroke-dashoffset: 0; }
                    }
                    @keyframes confettiFloat {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        50% { transform: translateY(-6px) rotate(8deg); }
                    }
                    @keyframes fadeUp {
                        0% { opacity: 0; transform: translateY(16px); }
                        100% { opacity: 1; transform: translateY(0); }
                    }
                `}</style>
            </div>
        );
    }

    if (showAuthModal) {
        return (
            <AuthPanel
                onAuthReady={(currentUser) => {
                    if (currentUser && onAuthReady) {
                        onAuthReady(currentUser);
                        setShowAuthModal(false);
                    }
                }}
                showSkip={false}
                isModal={true}
                onClose={() => setShowAuthModal(false)}
            />
        );
    }

    // Show NotServiceableScreen if address is not serviceable
    if (showNotServiceable && activeAddress) {
        return (
            <NotServiceableScreen
                selectedAddress={activeAddress}
                onChangeLocation={() => {
                    setShowNotServiceable(false);
                    if (onOpenAddresses) {
                        onOpenAddresses();
                    }
                }}
                onNotifyMe={() => {
                    alert('We will notify you when we start delivering to your area!');
                    setShowNotServiceable(false);
                }}
            />
        );
    }
    
    // Updated unauthenticated user view
    if (!user) {
        return (
            <div style={{ padding: '16px', background: '#f5f5f7', height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{
                    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    border: '1.5px solid #6ee7b7',
                    borderRadius: '16px',
                    padding: '24px',
                    textAlign: 'center',
                    boxShadow: '0 2px 8px rgba(16,185,129,0.12)',
                    maxWidth: '400px',
                    width: '100%',
                }}>
                    <div style={{ fontSize: '36px', marginBottom: '10px' }}>🔐</div>
                    <h2 style={{ margin: '0 0 6px', color: '#065f46', fontSize: '20px', fontWeight: '800' }}>Sign in to continue</h2>
                    <p style={{ margin: '0 0 18px', color: '#047857', fontSize: '14px', lineHeight: '1.5' }}>
                        Track your orders, save delivery addresses, and enjoy a seamless shopping experience.
                    </p>
                    <button
                        onClick={() => setShowAuthModal(true)}
                        style={{
                            width: '100%',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '14px',
                            fontSize: '15px',
                            fontWeight: '700',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
                        }}
                    >
                        Sign In / Sign Up
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#f5f5f7', overflow: 'hidden' }}>

            {/* Header */}
            <header style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                borderBottom: '1px solid #e5e7eb',
                padding: '10px 14px',
                paddingTop: 'calc(10px + var(--safe-area-top, 0px))',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexShrink: 0,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                position: 'sticky',
                top: 0,
                zIndex: 10,
            }}>
                <button
                    onClick={onClose}
                    style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#374151'
                    }}
                >
                    ←
                </button>
                <span style={{ fontSize: '17px', fontWeight: '800', color: '#111827', flex: 1 }}>My Cart</span>
                {cart.length > 0 && (
                    <span style={{
                        background: '#f3f4f6', border: '1px solid #e5e7eb',
                        borderRadius: '20px', padding: '3px 10px',
                        fontSize: '12px', fontWeight: '700', color: '#374151',
                    }}>{cart.length} item{cart.length !== 1 ? 's' : ''}</span>
                )}
            </header>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(140px + var(--bottom-nav-height, 64px) + env(safe-area-inset-bottom, 0px))' }}>

                {/* Hero */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--primary, #7c3aed) 0%, #5b21b6 100%)',
                    padding: '20px 20px 24px',
                    textAlign: 'center',
                }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🛒</div>
                    <h2 style={{ margin: '0 0 4px', color: 'white', fontSize: '18px', fontWeight: '800', letterSpacing: '-0.3px' }}>
                        {cart.length === 0 ? 'Your cart is empty' : 'Review your order'}
                    </h2>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>
                        {cart.length === 0 ? 'Add items to get started' : `₹${subtotal} subtotal · ${deliveryFee === 0 ? 'Free delivery' : `₹${deliveryFee} delivery`}`}
                    </p>
                </div>

                <div style={{ padding: '16px' }}>

                    {/* Cart items */}
                    {cart.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>Items</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {cart.map((item) => (
                                    <div key={item.id} style={{
                                        background: 'white', borderRadius: '14px',
                                        border: '1px solid #e5e7eb', padding: '12px',
                                        display: 'flex', alignItems: 'center', gap: '12px',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                                    }}>
                                        {item.image && (
                                            <img src={item.image} alt={item.name} style={{ width: '56px', height: '56px', borderRadius: '10px', objectFit: 'cover', flexShrink: 0 }} />
                                        )}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>
                                                {item.brand ? `${item.brand} ${item.name}` : item.name}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{item.unit} · {item.category}</div>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--primary, #7c3aed)' }}>₹{item.price}</div>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                    onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                                                    style={{ width: '28px', height: '28px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >−</button>
                                                <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827', minWidth: '16px', textAlign: 'center' }}>{item.quantity}</span>
                                                <button
                                                    onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                                                    style={{ width: '28px', height: '28px', border: '1px solid #e5e7eb', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                >+</button>
                                            </div>
                                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#111827' }}>₹{item.price * item.quantity}</div>
                                            <button onClick={() => onRemove(item.id)} style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Remove</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Delivery Address */}
                    {cart.length > 0 && (
                        <div style={{ marginBottom: '20px' }}>
                            <h3 style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>Delivery Address</h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {addresses.map((addr, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedAddress(idx)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            padding: '14px', textAlign: 'left',
                                            border: `1.5px solid ${selectedAddress === idx ? 'var(--primary, #ff6b35)' : '#e5e7eb'}`,
                                            borderRadius: '14px',
                                            background: selectedAddress === idx ? '#fff7ed' : 'white',
                                            cursor: 'pointer',
                                            boxShadow: selectedAddress === idx ? '0 2px 8px rgba(255,107,53,0.1)' : '0 1px 4px rgba(0,0,0,0.05)',
                                            transition: 'border-color 0.15s, background 0.15s',
                                        }}
                                    >
                                        <span style={{
                                            width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                                            border: `2px solid ${selectedAddress === idx ? 'var(--primary, #ff6b35)' : '#d1d5db'}`,
                                            background: selectedAddress === idx ? 'var(--primary, #ff6b35)' : 'white',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            {selectedAddress === idx && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'white', display: 'block' }} />}
                                        </span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '2px' }}>{addr.label}</div>
                                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{[addr.line1, addr.line2, addr.city, addr.pincode].filter(Boolean).join(', ')}</div>
                                        </div>
                                    </button>
                                ))}
                                {addresses.length === 0 && (
                                    <div style={{
                                        background: 'white', borderRadius: '14px', border: '1px solid #e5e7eb',
                                        padding: '16px', textAlign: 'center',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                                    }}>
                                        <div style={{ fontSize: '24px', marginBottom: '6px' }}>📍</div>
                                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '4px' }}>No saved addresses</div>
                                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '0' }}>Add an address to place your order</div>
                                    </div>
                                )}
                                {addresses.length < 10 && (
                                    <button
                                        onClick={onOpenAddresses}
                                        style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                            padding: '12px', border: '1.5px dashed #d1d5db', borderRadius: '14px',
                                            background: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                                            color: 'var(--primary, #ff6b35)', width: '100%',
                                        }}
                                    >+ Add Address</button>
                                )}
                                {addresses.length >= 10 && (
                                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
                                        Maximum 10 addresses reached
                                    </p>
                                )}
                            </div>
                            {selectedAddress !== null && deliveryPlan.estimatedTime && (
                                <div style={{
                                    marginTop: '8px', background: '#f0fdf4', border: '1px solid #bbf7d0',
                                    borderRadius: '10px', padding: '10px 14px',
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                }}>
                                    <span style={{ fontSize: '16px' }}>🚚</span>
                                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#065f46' }}>{deliveryPlan.estimatedTime}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Order Summary */}
                    {cart.length > 0 && (
                        <div style={{
                            background: 'white', borderRadius: '14px', border: '1px solid #e5e7eb',
                            padding: '16px', marginBottom: '20px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        }}>
                            <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '800', color: '#111827' }}>Order Summary</h3>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                                <span>Item Total</span><span>₹{subtotal}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', color: '#374151' }}>
                                <span>Delivery Fee</span>
                                {deliveryFee > 0
                                    ? <span>₹{deliveryFee}</span>
                                    : <span style={{ color: '#10b981', fontWeight: '700' }}>Free 🎉</span>
                                }
                            </div>
                            {MIN_ORDER_AMOUNT > 0 && subtotal < MIN_ORDER_AMOUNT && (
                                <div style={{
                                  fontSize: '12px',
                                  color: '#6b7280',
                                  marginBottom: '12px',
                                  background: '#fef9c3',
                                  borderRadius: '8px',
                                  padding: '6px 8px',
                                  textAlign: 'center',
                                  lineHeight: '1.4',
                                }}>
                                    Add ₹{MIN_ORDER_AMOUNT - subtotal} more for free delivery
                                </div>
                            )}
                            <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '800', color: '#111827' }}>
                                <span>To Pay</span><span>₹{cartTotal}</span>
                            </div>
                        </div>
                    )}

                    {orderError && (
                        <div style={{
                            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px',
                            padding: '10px 14px', marginBottom: '12px',
                            fontSize: '13px', color: '#dc2626', fontWeight: '600',
                        }}>{orderError}</div>
                    )}

                </div>
            </div>

            {/* Sticky bottom CTA */}
            {cart.length > 0 && (
                <div style={{
                    position: 'sticky', bottom: 0,
                    background: 'white', borderTop: '1px solid #f0f0f0',
                    padding: '12px 16px',
                    paddingBottom: 'calc(12px + var(--bottom-nav-height, 64px) + env(safe-area-inset-bottom, 0px))',
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.06)',
                }}>
                    <button
                        onClick={handlePlaceOrder}
                        disabled={isPlacing || !selectedSlot}
                        style={{
                            width: '100%', padding: '14px',
                            background: (!selectedSlot) ? '#d1d5db' : 'linear-gradient(135deg, var(--primary, #7c3aed), #5b21b6)',
                            color: 'white', border: 'none', borderRadius: '14px',
                            fontSize: '16px', fontWeight: '800', cursor: (!selectedSlot) ? 'not-allowed' : 'pointer',
                            boxShadow: (!selectedSlot) ? 'none' : '0 4px 12px rgba(124,58,237,0.3)',
                            transition: 'opacity 0.15s',
                        }}
                        onTouchStart={e => { if (selectedSlot) e.currentTarget.style.opacity = '0.85'; }}
                        onTouchEnd={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                        Place Order · ₹{cartTotal}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CartPanel;
